import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '../services/api';
import DateFilter from '../components/DateFilter';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const fmt = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v);
const PIE_COLORS = ['#059669', '#f59e0b'];

export default function HotelsPage() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async (params = {}, p = 1) => {
    setLoading(true);
    try {
      const res = await dashboardAPI.hotels({ ...params, page: p, limit: 20 });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  const handleFilter = (params) => { setFilters(params); setPage(1); load(params, 1); };
  const handlePage = (p) => { setPage(p); load(filters, p); };

  const filteredData = data?.data?.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.yolcu_adi || '').toLowerCase().includes(s) ||
           (r.otel_adi || '').toLowerCase().includes(s) ||
           (r.fatura_no || '').toLowerCase().includes(s);
  });

  if (loading && !data) return <div className="loading">Yükleniyor...</div>;

  return (
    <div>
      <div className="topbar">
        <h1>Otel Konaklama Analitik</h1>
        <DateFilter onFilter={handleFilter} />
      </div>

      {data && (
        <>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="label">Toplam Konaklama</div>
              <div className="value">{data.total}</div>
            </div>
            <div className="stat-card">
              <div className="label">Toplam Gece</div>
              <div className="value">{data.total_nights}</div>
            </div>
            <div className="stat-card">
              <div className="label">Toplam Tutar</div>
              <div className="value">{fmt(data.totalAmount || 0)}</div>
            </div>
            <div className="stat-card">
              <div className="label">Ort. Gece/Konaklama</div>
              <div className="value">{data.total > 0 ? (data.total_nights / data.total).toFixed(1) : '0'}</div>
            </div>
            <div className="stat-card">
              <div className="label">Gecelik Ort. Fiyat</div>
              <div className="value">{fmt(data.total_nights > 0 ? (data.totalAmount || 0) / data.total_nights : 0)}</div>
            </div>
          </div>

          {/* Split layout: Hotels chart left, Yurtiçi/Yurtdışı right */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="chart-card">
              <h3>En Çok Kullanılan Oteller</h3>
              <ResponsiveContainer width="100%" height={Math.max(220, (data.hotels?.length || 5) * 30)}>
                <BarChart data={data.hotels?.slice(0, 10)} layout="vertical" margin={{ left: 120, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={v => `${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="otel_adi" tick={{ fontSize: 10 }} width={115} />
                  <Tooltip formatter={(v, name) => [fmt(v), name === 'total' ? 'Tutar' : name === 'count' ? 'Adet' : name]} />
                  <Bar dataKey="total" name="Tutar" fill="#059669" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Yurtiçi / Yurtdışı Dağılımı</h3>
              {data.hatTipi && data.hatTipi.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={data.hatTipi}
                        dataKey="count"
                        nameKey="name"
                        cx="50%" cy="50%"
                        outerRadius={85}
                        label={({ name, percent }) => `${name} %${(percent * 100).toFixed(0)}`}
                        labelLine={{ strokeWidth: 1 }}
                      >
                        {data.hatTipi.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v, name, props) => [v + ' konaklama', props.payload.name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 24, marginTop: 8, fontSize: 13 }}>
                    {data.hatTipi.map((h, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 600, color: PIE_COLORS[i] }}>{h.name}</div>
                        <div>{h.count} konaklama</div>
                        <div style={{ fontWeight: 600 }}>{fmt(h.total)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Veri yok</div>}
            </div>
          </div>

          <div className="chart-card">
            <h3>Konaklama Detayları</h3>
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              <table className="data-table">
                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                  <tr>
                    <th>
                      Yolcu
                      <input type="text" placeholder="Ara..." value={search} onChange={e => setSearch(e.target.value)}
                        style={{ display: 'block', width: '100%', marginTop: 4, padding: '2px 6px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 4 }} />
                    </th>
                    <th>Otel</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Gece</th>
                    <th>Tutar</th>
                    <th>Gecelik Fiyat</th>
                    <th>Fatura No</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData?.map(r => (
                    <tr key={r.id}>
                      <td>{r.yolcu_adi}</td>
                      <td>{r.otel_adi}</td>
                      <td>{r.check_in?.slice(0, 10)}</td>
                      <td>{r.check_out?.slice(0, 10)}</td>
                      <td style={{ textAlign: 'center' }}>{(() => {
                        if (!r.check_in || !r.check_out) return '-';
                        const d = Math.round((new Date(r.check_out) - new Date(r.check_in)) / 86400000);
                        return d > 0 ? d : '-';
                      })()}</td>
                      <td style={{ fontWeight: 600 }}>{fmt(r.satis_toplam)}</td>
                      <td>{(() => {
                        if (!r.check_in || !r.check_out || !r.satis_toplam) return '-';
                        const d = Math.round((new Date(r.check_out) - new Date(r.check_in)) / 86400000);
                        return d > 0 ? fmt(r.satis_toplam / d) : '-';
                      })()}</td>
                      <td>{r.fatura_no}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.totalPages > 1 && (
              <div className="pagination">
                <button disabled={page <= 1} onClick={() => handlePage(page - 1)}>← Önceki</button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>{page} / {data.totalPages}</span>
                <button disabled={page >= data.totalPages} onClick={() => handlePage(page + 1)}>Sonraki →</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
